/**
 * Consolidated Export Builder
 *
 * Builds Excel export data for the Consolidated Projections view (locationId=0)
 * by reading from the exact same data sources as the screen rendering:
 * - viewData.departments[] for gross, expenses, profit totals
 * - expensesByAccount for expense account drill-down (matches renderDepartment lines 909-945)
 * - expensesByAccount for OTHERS income accounts (matches OthersIncomeContent lines 171-206)
 * - Frontend seasonality for monthly units (matches renderDepartment line 1042-1043)
 *
 * This ensures the exported spreadsheet matches the on-screen display exactly.
 */

import { MonthlyTotals, createEmptyMonthlyTotals, addMonthlyTotals } from '../types';
import { DOC_TEMPLATE } from '../config/docTemplate';
import { ProjectionViewData } from '../types/projectionViewData';
import { ExpensesByAccount } from '../components/projections/seasonalityModeTypes';
import {
  DepartmentExportData,
  ExportRow,
  ExportCategory,
  ExportAccount,
} from './projectionsExport';

/**
 * Convert a Record<string, number> monthlyDistribution to MonthlyTotals
 */
function toMonthlyTotals(dist: Record<string, number>): MonthlyTotals {
  return {
    month1: dist.month1 || 0,
    month2: dist.month2 || 0,
    month3: dist.month3 || 0,
    month4: dist.month4 || 0,
    month5: dist.month5 || 0,
    month6: dist.month6 || 0,
    month7: dist.month7 || 0,
    month8: dist.month8 || 0,
    month9: dist.month9 || 0,
    month10: dist.month10 || 0,
    month11: dist.month11 || 0,
    month12: dist.month12 || 0,
  };
}

/**
 * Sum monthly values to get an annual total
 */
function sumMonthly(monthly: MonthlyTotals): number {
  let total = 0;
  for (let m = 1; m <= 12; m++) {
    total += monthly[`month${m}` as keyof MonthlyTotals] || 0;
  }
  return total;
}

/**
 * Build department export data for MISC (uses calculationsMap, no viewData)
 */
function buildMiscDepartment(
  calculationsMap: Map<string, MonthlyTotals>,
): DepartmentExportData {
  const rows: ExportRow[] = [];

  const storedGross = calculationsMap.get('MISC#GROSS');
  if (storedGross) {
    const monthlyGross = { ...storedGross } as MonthlyTotals;
    rows.push({
      label: 'Total Gross',
      monthlyValues: monthlyGross,
      annualTotal: sumMonthly(monthlyGross),
      isTotalRow: true,
    });
  }

  const storedExpenses = calculationsMap.get('MISC#TOTAL_EXPENSES');
  if (storedExpenses) {
    const monthlyExpenses = { ...storedExpenses } as MonthlyTotals;
    rows.push({
      label: 'Total Expenses',
      monthlyValues: monthlyExpenses,
      annualTotal: sumMonthly(monthlyExpenses),
    });
  }

  return {
    name: 'MISC',
    displayName: 'Misc Clearing Accounts',
    rows,
  };
}

/**
 * Build OTHERS income accounts as an ExportCategory from expensesByAccount.
 * Matches OthersIncomeContent (DepartmentContentRenderers.tsx lines 171-206)
 */
function buildOthersIncomeCategory(
  expensesByAccount: ExpensesByAccount | undefined,
): ExportCategory | null {
  if (!expensesByAccount) return null;

  const othersDept = DOC_TEMPLATE.find(d => d.name === 'OTHERS');
  const incomeCategory = othersDept?.expenseCategories.find(c => c.name === 'OTHER_INCOME');
  if (!incomeCategory) return null;

  const accounts: ExportAccount[] = [];
  let categoryMonthly = createEmptyMonthlyTotals();
  let categoryAnnual = 0;

  for (const acc of incomeCategory.accounts) {
    const key = `OTHERS#INCOME_${acc.code}`;
    const preCalc = expensesByAccount[key];
    if (preCalc && preCalc.annualTotal !== 0) {
      const monthly = toMonthlyTotals(preCalc.monthlyDistribution);
      accounts.push({
        code: acc.code,
        description: acc.description,
        monthlyValues: monthly,
        annualTotal: preCalc.annualTotal,
      });
      categoryAnnual += preCalc.annualTotal;
      for (let m = 1; m <= 12; m++) {
        const k = `month${m}` as keyof MonthlyTotals;
        categoryMonthly[k] += monthly[k];
      }
    }
  }

  if (accounts.length === 0) return null;

  return {
    name: 'OTHER_INCOME',
    displayName: 'Other Income',
    monthlyValues: categoryMonthly,
    annualTotal: categoryAnnual,
    accounts,
  };
}

/**
 * Build expense categories from expensesByAccount for a department.
 * Matches the consolidated path in renderDepartment (SeasonalityModeView lines 909-945).
 */
function buildExpenseCategoriesFromExpensesByAccount(
  deptName: string,
  expensesByAccount: ExpensesByAccount | undefined,
): ExportCategory[] {
  if (!expensesByAccount) return [];

  const deptTemplate = DOC_TEMPLATE.find(d => d.name === deptName);
  if (!deptTemplate) return [];

  const categories: ExportCategory[] = [];

  deptTemplate.expenseCategories
    .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
    .forEach(cat => {
      let categoryAnnual = 0;
      let categoryMonthly = createEmptyMonthlyTotals();
      const exportAccounts: ExportAccount[] = [];

      cat.accounts.forEach(acc => {
        const expenseKey = `${deptName}#EXPENSE_${acc.code}`;
        const preCalculated = expensesByAccount[expenseKey];
        if (preCalculated) {
          const accMonthly = toMonthlyTotals(preCalculated.monthlyDistribution);
          categoryAnnual += preCalculated.annualTotal;
          categoryMonthly = addMonthlyTotals(categoryMonthly, accMonthly);

          if (preCalculated.annualTotal !== 0) {
            exportAccounts.push({
              code: acc.code,
              description: acc.description,
              monthlyValues: accMonthly,
              annualTotal: preCalculated.annualTotal,
            });
          }
        }
      });

      if (categoryAnnual > 0 || exportAccounts.length > 0) {
        categories.push({
          name: cat.name,
          displayName: cat.displayName,
          monthlyValues: categoryMonthly,
          annualTotal: categoryAnnual,
          accounts: exportAccounts,
        });
      }
    });

  return categories;
}

/**
 * Build export data for the consolidated projections view.
 * Reads from the exact same data sources as the screen rendering.
 *
 * @param viewData - Backend-computed view data (for gross, expenses, profit totals)
 * @param expensesByAccount - Pre-calculated expense/income amounts (for account drill-down)
 * @param calculationsMap - Stored calculations (for MISC department only)
 * @param getTotalProjectedUnits - Same function used by renderDepartment for unit totals
 * @param getDeptUnitSeasonalityWeights - Same function used by renderDepartment for monthly unit distribution
 * @param applySeasonalityFn - Same applySeasonality function used by renderDepartment
 */
export function buildConsolidatedExportData(
  viewData: ProjectionViewData,
  expensesByAccount: ExpensesByAccount | undefined,
  calculationsMap: Map<string, MonthlyTotals>,
  getTotalProjectedUnits: (department: string) => number,
  getDeptUnitSeasonalityWeights: (department: string) => number[],
  applySeasonalityFn: (amount: number, weights: number[]) => MonthlyTotals,
): DepartmentExportData[] {
  return DOC_TEMPLATE.map(dept => {
    // MISC is not in viewData — use calculationsMap
    if (dept.name === 'MISC') {
      return buildMiscDepartment(calculationsMap);
    }

    // Find this department in viewData
    const viewDataDept = viewData.departments.find(d => d.department === dept.name);
    if (!viewDataDept) {
      return {
        name: dept.name,
        displayName: `${dept.displayName} Department`,
        rows: [],
      };
    }

    const rows: ExportRow[] = [];

    // === GROSS ROWS ===
    if (dept.hasFandI) {
      // Vehicle departments: Front-End Gross, F&I Income, Total Gross
      if (viewDataDept.frontGross !== undefined && viewDataDept.monthlyFrontGross) {
        rows.push({
          label: 'Front-End Gross',
          monthlyValues: viewDataDept.monthlyFrontGross,
          annualTotal: viewDataDept.frontGross,
          indent: 1,
        });
      }
      if (viewDataDept.fiGross !== undefined && viewDataDept.monthlyFiGross) {
        rows.push({
          label: 'F&I Income',
          monthlyValues: viewDataDept.monthlyFiGross,
          annualTotal: viewDataDept.fiGross,
          indent: 1,
        });
      }
      rows.push({
        label: 'Total Gross',
        monthlyValues: viewDataDept.monthlyGross,
        annualTotal: viewDataDept.projectedGross,
        isTotalRow: true,
      });
    } else if (dept.name === 'RENTAL') {
      rows.push({
        label: 'Rental Income',
        monthlyValues: viewDataDept.monthlyGross,
        annualTotal: viewDataDept.projectedGross,
        isTotalRow: true,
      });
    } else if (dept.name === 'OTHERS') {
      rows.push({
        label: 'Other Income',
        monthlyValues: viewDataDept.monthlyGross,
        annualTotal: viewDataDept.projectedGross,
        isTotalRow: true,
      });
    } else {
      // SERVICE, PARTS, BODY_SHOP
      rows.push({
        label: 'Total Gross',
        monthlyValues: viewDataDept.monthlyGross,
        annualTotal: viewDataDept.projectedGross,
        isTotalRow: true,
      });
    }

    // === UNITS (vehicle departments only) ===
    // Use the same calculation as the screen: getTotalProjectedUnits + applySeasonality(deptUnitWeights)
    // This matches renderDepartment lines 1006, 1042-1043
    if (dept.hasFandI) {
      const totalUnits = getTotalProjectedUnits(dept.name);
      if (totalUnits > 0) {
        const deptUnitWeights = getDeptUnitSeasonalityWeights(dept.name);
        const monthlyUnits = applySeasonalityFn(totalUnits, deptUnitWeights);
        rows.push({
          label: 'Units',
          monthlyValues: monthlyUnits,
          annualTotal: totalUnits,
          isUnits: true,
        });
      }
    }

    // === EXPENSE / INCOME CATEGORIES ===
    const exportCategories: ExportCategory[] = [];

    if (dept.name === 'OTHERS') {
      // OTHERS: income account breakdown from expensesByAccount
      const incomeCategory = buildOthersIncomeCategory(expensesByAccount);
      if (incomeCategory) {
        exportCategories.push(incomeCategory);
      }
    } else {
      // All other departments: expense categories from expensesByAccount
      // This matches renderDepartment consolidated path (lines 909-945)
      const cats = buildExpenseCategoriesFromExpensesByAccount(dept.name, expensesByAccount);
      exportCategories.push(...cats);
    }

    // === TOTAL EXPENSES (skip for OTHERS — income-only) ===
    if (dept.name !== 'OTHERS') {
      rows.push({
        label: 'Total Expenses',
        monthlyValues: viewDataDept.monthlyExpenses,
        annualTotal: viewDataDept.projectedExpenses,
      });
    }

    // === OPERATING PROFIT (skip for OTHERS) ===
    if (dept.name !== 'OTHERS') {
      rows.push({
        label: 'Operating Profit',
        monthlyValues: viewDataDept.monthlyProfit,
        annualTotal: viewDataDept.projectedProfit,
        isTotalRow: true,
      });
    }

    return {
      name: dept.name,
      displayName: dept.name === 'OTHERS' ? dept.displayName : `${dept.displayName} Department`,
      rows,
      expenseCategories: exportCategories.length > 0 ? exportCategories : undefined,
    };
  });
}
